import { apiClient } from '@/lib/api/client'

export type FulfillmentStatus =
  | 'DRAFT' | 'SUBMITTED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETE' | 'CANCELLED' | 'ERROR'

export interface FulfillmentRecipientAddress {
  line1?: string
  line2?: string | null
  townOrCity?: string
  stateOrCounty?: string | null
  postalOrZipCode?: string
  countryCode?: string
}

export interface FulfillmentRecipient {
  name?: string
  email?: string | null
  phoneNumber?: string | null
  address?: FulfillmentRecipientAddress
}

export interface FulfillmentOrder {
  id: number
  provider: string
  providerOrderId: string | null
  merchantReference: string | null
  shopifyOrderId: string | null
  idempotencyKey: string | null
  status: FulfillmentStatus
  stage: string | null
  stageDetails: Record<string, string> | null
  issues: any[] | null
  shippingMethod: string | null
  recipient: FulfillmentRecipient | null
  totalCost: string | number | null
  totalTax: string | number | null
  currency: string | null
  callbackUrl: string | null
  raw?: any
  providerCreatedAt: string | null
  providerUpdatedAt: string | null
  submittedAt: string | null
  completedAt: string | null
  createdAt: string
  updatedAt: string
  // Derived (list view only)
  itemCount?: string | number
  trackingNumber?: string | null
  carrierName?: string | null
}

export interface FulfillmentItem {
  id: number
  fulfillmentOrderId: number
  providerItemId: string | null
  merchantReference: string | null
  internalSku: string | null
  providerSku: string
  artworkId: number | null
  copies: number
  sizing: string | null
  attributes: Record<string, any> | null
  paperType: string | null
  substrateWeight: string | null
  assetUrl: string | null
  thumbnailUrl: string | null
  itemCost: string | number | null
  currency: string | null
  status: string | null
}

export interface FulfillmentShipment {
  id: number
  fulfillmentOrderId: number
  providerShipmentId: string | null
  carrierName: string | null
  carrierService: string | null
  trackingNumber: string | null
  trackingUrl: string | null
  dispatchDate: string | null
  fulfillmentCountry: string | null
  fulfillmentLab: string | null
  status: string | null
}

export interface FulfillmentOrderDetail {
  order: FulfillmentOrder
  items: FulfillmentItem[]
  shipments: FulfillmentShipment[]
}

export interface FulfillmentSummary {
  total: number
  in_flight: number
  complete: number
  problem: number
  providers: string[]
  submitEnabled?: boolean
}

export interface FulfillmentDraftItem {
  merchantReference: string
  internalSku: string
  sku: string
  copies: number
  sizing: string
  paperType: string | null
  substrateWeight: string | null
  assetGenerated: boolean
  attributes: Record<string, any>
}

export interface FulfillmentSubmitResult {
  submitted: boolean
  reason?: string
  outcome?: string
  providerOrderId?: string
  order?: FulfillmentOrder
  draft?: {
    merchantReference: string
    idempotencyKey: string
    shippingMethod: string
    callbackUrl: string | null
    items: FulfillmentDraftItem[]
  }
  payload?: any
}

export interface FulfillmentCancelResult {
  outcome?: string
  order: FulfillmentOrder
}

export interface FulfillmentListParams {
  start?: number
  limit?: number
  status?: string
  provider?: string
  country?: string
  search?: string
}

export const fulfillmentApi = {
  getOrders: async (params: FulfillmentListParams = {}) => {
    const response = await apiClient.get<FulfillmentOrder[]>('/api/fulfillment', {
      params: {
        start: params.start || 0,
        limit: params.limit || 20,
        status: params.status,
        provider: params.provider,
        country: params.country,
        search: params.search,
      },
    })
    const total = parseInt(response.headers['count'] || '0', 10)
    return {
      data: response.data || [],
      total,
      start: params.start || 0,
      limit: params.limit || 20,
    }
  },

  getSummary: async (): Promise<FulfillmentSummary> => {
    const response = await apiClient.get<FulfillmentSummary>('/api/fulfillment/summary')
    return response.data
  },

  getOrder: async (id: number): Promise<FulfillmentOrderDetail> => {
    const response = await apiClient.get<FulfillmentOrderDetail>(`/api/fulfillment/${id}`)
    return response.data
  },

  refresh: async (id: number): Promise<FulfillmentOrderDetail> => {
    const response = await apiClient.post<FulfillmentOrderDetail>(`/api/fulfillment/${id}/refresh`)
    return response.data
  },

  // Build (dryRun) or submit (dryRun:false) a Shopify order to the provider.
  submit: async (shopifyOrderId: string | number, dryRun = true, provider?: string): Promise<FulfillmentSubmitResult> => {
    const response = await apiClient.post<FulfillmentSubmitResult>('/api/fulfillment/submit', {
      shopifyOrderId, dryRun, provider,
    })
    return response.data
  },

  cancel: async (id: number): Promise<FulfillmentCancelResult> => {
    const response = await apiClient.post<FulfillmentCancelResult>(`/api/fulfillment/${id}/cancel`)
    return response.data
  },
}
